import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { getSelfServiceContentFallback } from '../data/selfServiceContent';
import { hydrateDocumentColumns } from '../data/dashboardContent';

const WEEKDAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma'];

const emptyMenuRow = (day) => ({
  day,
  soup: '',
  main: '',
  side: '',
  extra: '',
  serviceWindow: '12:00 - 14:00'
});

// Yüklenen menüyü her zaman Pzt–Cuma tam liste olacak şekilde normalize et.
const normalizeLunchMenu = (menu = []) =>
  WEEKDAYS.map((day) => {
    const existing = menu.find((item) => item.day === day);
    return existing ? { ...emptyMenuRow(day), ...existing, day } : emptyMenuRow(day);
  });

const ContentAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [selfService, setSelfService] = useState({});
  const [lunchMenu, setLunchMenu] = useState(() => normalizeLunchMenu());
  const [documentColumns, setDocumentColumns] = useState(() => hydrateDocumentColumns());
  const [savingMenu, setSavingMenu] = useState(false);
  const [savingDocs, setSavingDocs] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }

  const activeCompanyId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      if (user.role === 'super_admin') {
        return localStorage.getItem('activeCompanyId') || '';
      }
      return user.companyId || '';
    } catch (error) {
      return '';
    }
  }, []);

  useEffect(() => {
    let active = true;

    const loadContent = async () => {
      setLoading(true);
      try {
        const response = await api.getManagedContent();
        if (!active) {
          return;
        }
        const data = response?.data || {};
        const loadedSelfService = data.selfService || {};
        setSelfService(loadedSelfService);
        setLunchMenu(
          normalizeLunchMenu(
            loadedSelfService.lunchMenu?.length
              ? loadedSelfService.lunchMenu
              : getSelfServiceContentFallback().lunchMenu
          )
        );
        setDocumentColumns(hydrateDocumentColumns(data.dashboard?.documentColumns));
      } catch (error) {
        // Henüz firmaya ait yönetilen içerik yoksa varsayılanlarla başla.
        if (active) {
          setSelfService({});
          setLunchMenu(normalizeLunchMenu(getSelfServiceContentFallback().lunchMenu));
          setDocumentColumns(hydrateDocumentColumns());
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadContent();
    return () => {
      active = false;
    };
  }, [activeCompanyId]);

  const updateMenuField = (index, field, value) => {
    setLunchMenu((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
    );
  };

  const handleSaveMenu = async () => {
    setSavingMenu(true);
    setFeedback(null);
    try {
      await api.updateManagedContent('selfService', {
        payload: { ...selfService, lunchMenu }
      });
      setSelfService((current) => ({ ...current, lunchMenu }));
      setFeedback({ type: 'success', message: 'Yemekhane menüsü kaydedildi.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error?.message || 'Menü kaydedilemedi.' });
    } finally {
      setSavingMenu(false);
    }
  };

  const updateColumnField = (colIndex, field, value) => {
    setDocumentColumns((current) =>
      current.map((col, index) => (index === colIndex ? { ...col, [field]: value } : col))
    );
  };

  const updateLinkField = (colIndex, linkIndex, field, value) => {
    setDocumentColumns((current) =>
      current.map((col, index) => {
        if (index !== colIndex) {
          return col;
        }
        return {
          ...col,
          links: col.links.map((link, lIndex) =>
            lIndex === linkIndex ? { ...link, [field]: value } : link
          )
        };
      })
    );
  };

  const addLink = (colIndex) => {
    setDocumentColumns((current) =>
      current.map((col, index) =>
        index === colIndex
          ? { ...col, links: [...col.links, { label: '', path: '', featured: false }] }
          : col
      )
    );
  };

  const removeLink = (colIndex, linkIndex) => {
    setDocumentColumns((current) =>
      current.map((col, index) =>
        index === colIndex
          ? { ...col, links: col.links.filter((_, lIndex) => lIndex !== linkIndex) }
          : col
      )
    );
  };

  const handleSaveDocs = async () => {
    setSavingDocs(true);
    setFeedback(null);
    try {
      await api.updateManagedContent('dashboard', {
        payload: { documentColumns }
      });
      setFeedback({ type: 'success', message: 'Öne çıkan dokümanlar kaydedildi.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error?.message || 'Dokümanlar kaydedilemedi.' });
    } finally {
      setSavingDocs(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid content-admin">
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <h3 className="mb-2">İçerik Yönetimi</h3>
            </div>
          </div>
        </div>
      </div>

      {feedback && (
        <div
          className={`alert ${feedback.type === 'success' ? 'alert-success' : 'alert-danger'}`}
          role="alert"
        >
          {feedback.message}
        </div>
      )}

      {/* --- Yemekhane Menüsü --- */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Yemekhane Menüsü</h4>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveMenu}
                  disabled={savingMenu}
                >
                  {savingMenu ? 'Kaydediliyor…' : 'Menüyü Kaydet'}
                </button>
              </div>
              <div className="table-responsive">
                <table className="table align-middle content-admin-table">
                  <thead>
                    <tr>
                      <th>Gün</th>
                      <th>Çorba</th>
                      <th>Ana Yemek</th>
                      <th>Yan Yemek</th>
                      <th>Ekstra</th>
                      <th>Servis Saati</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lunchMenu.map((row, index) => (
                      <tr key={row.day}>
                        <td className="fw-semibold">{row.day}</td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.soup}
                            onChange={(event) => updateMenuField(index, 'soup', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.main}
                            onChange={(event) => updateMenuField(index, 'main', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.side}
                            onChange={(event) => updateMenuField(index, 'side', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.extra}
                            onChange={(event) => updateMenuField(index, 'extra', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.serviceWindow}
                            onChange={(event) =>
                              updateMenuField(index, 'serviceWindow', event.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Öne Çıkan Dokümanlar --- */}
      <div className="row">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Öne Çıkan Form / Prosedür / Dokümanlar</h4>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveDocs}
                  disabled={savingDocs}
                >
                  {savingDocs ? 'Kaydediliyor…' : 'Dokümanları Kaydet'}
                </button>
              </div>

              <div className="row g-3">
                {documentColumns.map((column, colIndex) => (
                  <div key={column.key} className="col-lg-4">
                    <div className="content-admin-col">
                      <label className="form-label fw-semibold">Kart Başlığı</label>
                      <input
                        className="form-control mb-3"
                        value={column.title}
                        onChange={(event) =>
                          updateColumnField(colIndex, 'title', event.target.value)
                        }
                      />

                      {column.links.map((link, linkIndex) => (
                        <div key={linkIndex} className="content-admin-link">
                          <input
                            className="form-control form-control-sm mb-1"
                            placeholder="Bağlantı etiketi"
                            value={link.label}
                            onChange={(event) =>
                              updateLinkField(colIndex, linkIndex, 'label', event.target.value)
                            }
                          />
                          <input
                            className="form-control form-control-sm mb-1"
                            placeholder="Bağlantı yolu (ör. /dokuman-merkezi?kategori=...)"
                            value={link.path}
                            onChange={(event) =>
                              updateLinkField(colIndex, linkIndex, 'path', event.target.value)
                            }
                          />
                          <div className="d-flex justify-content-between align-items-center">
                            <label className="form-check-label d-flex align-items-center gap-1">
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={Boolean(link.featured)}
                                onChange={(event) =>
                                  updateLinkField(
                                    colIndex,
                                    linkIndex,
                                    'featured',
                                    event.target.checked
                                  )
                                }
                              />
                              <span className="small">Öne çıkar</span>
                            </label>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => removeLink(colIndex, linkIndex)}
                            >
                              Kaldır
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => addLink(colIndex)}
                      >
                        + Bağlantı ekle
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentAdminPage;
